import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchSlots,
  createBooking,
  ApiError,
  type BusinessInfo,
  type ServiceInfo,
  type Slot,
} from "../lib/api.ts";
import { BusinessHeader } from "../components/BusinessHeader.tsx";
import { ServiceList } from "../components/ServiceList.tsx";
import { DatePicker } from "../components/DatePicker.tsx";
import { SlotGrid } from "../components/SlotGrid.tsx";
import { CustomerForm, type CustomerFormData } from "../components/CustomerForm.tsx";
import { ConfirmSummary } from "../components/ConfirmSummary.tsx";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  slug: string;
  business: BusinessInfo;
  services: ServiceInfo[];
  staff: Array<{ id: string; name: string }>;
}

type Step = "date" | "available" | "details" | "confirm";

// ─── Component ───────────────────────────────────────────────────────────────

export function DateFirstFlow({ slug, business, services }: Props) {
  const navigate = useNavigate();

  const today = new Date().toISOString().split("T")[0] ?? "";
  const [step, setStep] = useState<Step>("date");
  const [date, setDate] = useState<string>(today);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [customerData, setCustomerData] = useState<CustomerFormData | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Fetch slots whenever the selected services change on the "available" step
  useEffect(() => {
    if (step !== "available" || selectedServiceIds.length === 0) {
      setSlots([]);
      return;
    }
    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);
    fetchSlots(slug, date, selectedServiceIds)
      .then(setSlots)
      .catch(() => setSlotsError("Failed to load available slots."))
      .finally(() => setSlotsLoading(false));
  }, [step, date, selectedServiceIds, slug]);

  function toggleService(id: string) {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }

  function handleCustomerSubmit(data: CustomerFormData) {
    setCustomerData(data);
    setStep("confirm");
  }

  async function handleConfirm() {
    if (!selectedSlot || !customerData) return;
    setBookingLoading(true);
    setBookingError(null);
    try {
      const confirmation = await createBooking(slug, {
        service_ids: selectedServiceIds,
        staff_id: undefined,
        date: date,
        start_time: selectedSlot.start,
        meeting_mode: primaryMode,
        customer: {
          name: customerData.name,
          email: customerData.email,
          phone: customerData.phone,
          notes: customerData.notes || undefined,
        },
      });
      if (confirmation.checkout_url) {
        window.location.href = confirmation.checkout_url;
        return;
      }

      const serviceNames = selectedServices.map((s) => s.name).join(", ");
      const params = new URLSearchParams({
        token: confirmation.cancel_token,
        id: confirmation.appointment_id,
        date: date,
        start_time: selectedSlot.start,
        service: serviceNames,
        staff: "",
        meeting_mode: primaryMode,
      });
      navigate(`/${slug}/success?${params.toString()}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setBookingError("That time was just taken. Please pick another slot.");
        setStep("available");
        setSelectedSlot(null);
      } else {
        setBookingError("Booking failed. Please try again.");
      }
    } finally {
      setBookingLoading(false);
    }
  }

  // Derived data
  const selectedServices = services.filter((s) => selectedServiceIds.includes(s.id));
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration_min, 0);
  const totalPrice = selectedServices.reduce((sum, s) => sum + (s.price_cents ?? 0), 0);
  const primaryCurrency = selectedServices[0]?.currency ?? "USD";
  const primaryMode = selectedServices[0]?.meeting_mode ?? "in_person";
  const meetingModes = [...new Set(services.map((s) => s.meeting_mode))];

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "var(--font)" }}>
      <BusinessHeader business={business} meetingModes={meetingModes} />

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">

        {/* ── Step 1: Pick Date ── */}
        {step === "date" && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Choose a date</h2>
              <p className="text-sm text-gray-500 mt-1">Select when you'd like to come in.</p>
            </div>
            <DatePicker
              selected={date}
              onSelect={(d) => {
                setDate(d);
                setStep("available");
              }}
            />
          </div>
        )}

        {/* ── Step 2: Services + Slots ── */}
        {step === "available" && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setStep("date");
                  setSelectedServiceIds([]);
                  setSelectedSlot(null);
                  setSlots([]);
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                &larr; Back
              </button>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Pick your services</h2>
                <p className="text-sm text-gray-500">
                  {new Date(date + "T00:00:00").toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            <ServiceList
              services={services}
              selected={selectedServiceIds}
              onToggle={toggleService}
            />

            {selectedServiceIds.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">Available times</p>
                <SlotGrid
                  slots={slots}
                  selected={selectedSlot?.start ?? null}
                  onSelect={setSelectedSlot}
                  loading={slotsLoading}
                  error={slotsError}
                />
                {selectedSlot && (
                  <button
                    type="button"
                    onClick={() => setStep("details")}
                    className="w-full py-3 px-4 rounded-[var(--radius)] text-white text-sm font-semibold transition-opacity hover:opacity-90"
                    style={{ background: "var(--accent)", borderRadius: "var(--radius)" }}
                  >
                    Continue
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Customer Details ── */}
        {step === "details" && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Your details</h2>
            <CustomerForm
              onSubmit={handleCustomerSubmit}
              onBack={() => setStep("available")}
            />
          </div>
        )}

        {/* ── Step 4: Confirm ── */}
        {step === "confirm" && selectedSlot && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Review your booking</h2>
            <ConfirmSummary
              serviceNames={selectedServices.map((s) => s.name)}
              staffName={null}
              date={date}
              time={selectedSlot.start}
              durationMin={totalDuration}
              priceCents={totalPrice > 0 ? totalPrice : null}
              currency={primaryCurrency}
              meetingMode={primaryMode}
              onConfirm={handleConfirm}
              onBack={() => setStep("details")}
              loading={bookingLoading}
              error={bookingError}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-xs text-gray-400">
        Powered by{" "}
        <a
          href="https://zedule.app"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium hover:text-gray-600 transition-colors"
        >
          Zedule
        </a>
      </div>
    </div>
  );
}
